import React, { useEffect, useRef, useState } from "react";
import { MapPin, X } from "lucide-react";

// Lazy-loaded Leaflet CSS via CDN to avoid webpack asset processing quirks
const LEAFLET_CSS_ID = "leaflet-css";

function ensureLeafletCss() {
  if (typeof document === "undefined") return;
  if (document.getElementById(LEAFLET_CSS_ID)) return;
  const link = document.createElement("link");
  link.id = LEAFLET_CSS_ID;
  link.rel = "stylesheet";
  link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
  link.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
  link.crossOrigin = "";
  document.head.appendChild(link);
}

const DAMASCUS = [33.5138, 36.2765];

const LocationPicker = ({ value, onChange, testidPrefix = "loc", title = "حدّد موقع محلك", label = "تحديد الموقع على جوجل ماب" }) => {
  const [open, setOpen] = useState(false);
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const [pending, setPending] = useState(value || null);

  useEffect(() => { ensureLeafletCss(); }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current) return;

      const initial = pending
        ? [pending.lat, pending.lng]
        : DAMASCUS;

      const map = L.map(mapRef.current).setView(initial, pending ? 15 : 7);
      mapObjRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '© OpenStreetMap',
      }).addTo(map);

      // Custom marker icon (default paths break in webpack)
      const iconHtml = `<div style="background:#09637E;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>`;
      const icon = L.divIcon({ html: iconHtml, iconSize: [28, 28], iconAnchor: [14, 28], className: "" });

      const placeMarker = (latlng) => {
        if (markerRef.current) markerRef.current.remove();
        markerRef.current = L.marker(latlng, { icon }).addTo(map);
      };
      if (pending) placeMarker([pending.lat, pending.lng]);

      map.on("click", (e) => {
        const { lat, lng } = e.latlng;
        placeMarker(e.latlng);
        setPending({ lat, lng });
      });

      setTimeout(() => map.invalidateSize(), 100);
    })();

    return () => {
      cancelled = true;
      if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }
      if (markerRef.current) { markerRef.current = null; }
    };
    // Intentional: map is initialized once per modal open. `pending` mutations
    // during click do NOT need to re-run this effect (marker is updated imperatively).
  }, [open]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPending(p);
        if (mapObjRef.current) {
          mapObjRef.current.setView([p.lat, p.lng], 16);
        }
      },
      () => { /* silent */ }
    );
  };

  const confirm = () => {
    if (pending) onChange(pending);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null);
    setPending(null);
  };

  const gmapsLink = value ? `https://www.google.com/maps?q=${value.lat},${value.lng}` : null;

  return (
    <div>
      <button
        type="button"
        onClick={() => { setPending(value || null); setOpen(true); }}
        data-testid={`${testidPrefix}-open`}
        className="w-full h-12 rounded-lg border-2 border-dashed border-[#7AB2B2] hover:border-[#09637E] hover:bg-[#EBF4F6] text-[#09637E] font-bold flex items-center justify-center gap-2 transition-colors"
      >
        <MapPin size={18} />
        {value ? "تعديل الموقع على الخريطة" : label}
      </button>
      {value && (
        <div className="mt-2 flex items-center justify-between bg-[#EBF4F6] rounded-lg p-3 text-sm" data-testid={`${testidPrefix}-pill`}>
          <div className="flex flex-col">
            <span className="text-[#09637E] font-bold">✓ الموقع محدَّد</span>
            <a href={gmapsLink} target="_blank" rel="noopener noreferrer" className="text-xs text-[#088395] hover:underline">
              فتح في جوجل ماب ({value.lat.toFixed(5)}, {value.lng.toFixed(5)})
            </a>
          </div>
          <button type="button" onClick={clear} className="text-red-500 font-bold text-xs px-2 py-1 hover:bg-red-50 rounded" data-testid={`${testidPrefix}-clear`}>
            إزالة
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)} dir="rtl">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`${testidPrefix}-modal`}>
            <div className="p-4 border-b border-[#EBF4F6] flex items-center justify-between">
              <div className="flex items-center gap-2 font-extrabold text-[#333333]">
                <MapPin className="text-[#09637E]" size={20} /> {title}
              </div>
              <button onClick={() => setOpen(false)} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
            </div>
            <div className="p-3 text-xs text-[#666666] border-b border-[#EBF4F6]">
              اضغط على الخريطة لوضع دبوس الموقع، أو اضغط &quot;موقعي الحالي&quot; لاستخدام GPS.
            </div>
            <div ref={mapRef} className="flex-1 min-h-[400px]" data-testid={`${testidPrefix}-map`} />
            <div className="p-3 flex flex-col sm:flex-row gap-2 border-t border-[#EBF4F6]">
              <button type="button" onClick={useMyLocation} data-testid={`${testidPrefix}-mylocation`}
                className="h-11 px-4 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold hover:bg-[#EBF4F6]">
                📍 موقعي الحالي
              </button>
              <div className="flex-1 text-sm text-[#666666] self-center px-2 truncate">
                {pending ? `الموقع المختار: ${pending.lat.toFixed(5)}, ${pending.lng.toFixed(5)}` : "لم يتم اختيار موقع بعد"}
              </div>
              <button type="button" onClick={confirm} disabled={!pending} data-testid={`${testidPrefix}-confirm`}
                className="h-11 px-6 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold disabled:opacity-50 disabled:cursor-not-allowed">
                تأكيد الموقع
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
